import {
    AmountUnit,
    Button,
    CellBottleAndCase,
    CollectionColumns,
    CollectionItems,
    CollectionView,
    Icon,
    Link,
} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {to_specs} from '@bitstillery/common/lib/specs'
import {format_delivery_period, format_money_with_symbol} from '@bitstillery/common/lib/format'
import {classes} from '@bitstillery/common/lib/utils'
import {$t} from '@bitstillery/common/app'
import {CollectionProxy} from '@bitstillery/common/lib/collection'
import {dayjs} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'

import type {
    PortalOrderItemResponse,
    SalesOrderAdditionalResponse,
    SalesOrderCreditItemResponse,
    SalesOrderItemResponse,
    SalesOrderTBOItemResponse,
} from '@bitstillery/common/api/fact2server'
import type {OrderType} from './proforma'
import type {CollectionColumnSetup, CollectionRow} from '@bitstillery/common/lib/collection'

import {AvailabilityType} from '@bitstillery/common/api/fact2server'

import {status_icon_entry_for_type} from '@/components/collection/product/product'

/** A cart line is priced by its `list_price`, a booked line by its `price_per_case`; `is_cart` says which. */
type OrderLinePrice = Pick<SalesOrderItemResponse, 'case_number_of_bottles' | 'number_of_cases' | 'portal_comment'> &
    Partial<Pick<PortalOrderItemResponse, 'list_price'> & Pick<SalesOrderItemResponse, 'price_per_case'>>

const collection_cart = new CollectionProxy<PortalOrderItemResponse>()
const collection_stock = new CollectionProxy<SalesOrderItemResponse>()
const collection_arriving = new CollectionProxy<SalesOrderItemResponse>()
const collection_tbo = new CollectionProxy<SalesOrderTBOItemResponse>()
const collection_credit = new CollectionProxy<SalesOrderCreditItemResponse>()
const collection_additionals = new CollectionProxy<SalesOrderAdditionalResponse>()

interface OrderLinesAttrs {
    order: OrderType
}

function by_product_name(a: {product_name: string}, b: {product_name: string}): number {
    const _a = a.product_name.toUpperCase()
    const _b = b.product_name.toUpperCase()
    if (_a < _b) {
        return -1
    }
    if (_a > _b) {
        return 1
    }
    return 0
}

function replace_items<Row extends CollectionRow>(collection: CollectionProxy<Row>, items: Row[]) {
    collection.state.items.splice(0, collection.state.items.length, ...items)
}

export class OrderLines extends MithrilComponent<OrderLinesAttrs> {
    // Written in a view, the collections would notify the view that reads them and redraw it forever.
    private filled_order: OrderType | null = null

    fill_collections(order: OrderType) {
        if (order === this.filled_order) {
            return
        }
        this.filled_order = order

        const stock_items: SalesOrderItemResponse[] = []
        const purchase_items: SalesOrderItemResponse[] = []
        for (const item of order.sales_order_items) {
            if (item.offer_item_type === AvailabilityType.Purchase) {
                purchase_items.push(item)
            } else {
                stock_items.push(item)
            }
        }

        replace_items(collection_cart, order.portal_order_items)
        replace_items(collection_stock, stock_items.toSorted(by_product_name))
        replace_items(collection_arriving, purchase_items.toSorted(by_product_name))
        replace_items(collection_tbo, order.sales_order_tbo_items.toSorted(by_product_name))
        replace_items(collection_additionals, order.sales_order_additionals)
        replace_items(collection_credit, order.sales_order_credit_items)
    }

    override oninit(vnode: m.Vnode<OrderLinesAttrs>) {
        this.fill_collections(vnode.attrs.order)
    }

    override onbeforeupdate(vnode: m.Vnode<OrderLinesAttrs>) {
        this.fill_collections(vnode.attrs.order)
    }

    // Single source of truth for a line's availability: one branch decides both
    // the status icon and its tip, so the two can never disagree. An ETA means the
    // line is incoming (arriving/truck), never to-be-ordered.
    availability(
        row: Pick<SalesOrderItemResponse, 'delivery_period' | 'offer_item_type'> &
            Partial<Pick<SalesOrderTBOItemResponse, 'expected_delivery_date'>>,
    ): {icon: string; type: string; label: string} {
        const arriving = (label: string) => ({...status_icon_entry_for_type(AvailabilityType.Purchase), label})

        if (row.expected_delivery_date) {
            const period = dayjs(row.expected_delivery_date).diff(dayjs(), 'week') + 1
            return arriving($t('filters.types.availability.eta', {period}))
        }
        if (row.delivery_period && !Number.isNaN(Number(row.delivery_period))) {
            return arriving($t('filters.types.availability.eta', {period: format_delivery_period(Number(row.delivery_period))}))
        }
        if (row.offer_item_type === AvailabilityType.Stock) {
            return {...status_icon_entry_for_type(AvailabilityType.Stock), label: $t('filters.types.availability.stock')}
        }
        return {...status_icon_entry_for_type(AvailabilityType.Tbo), label: $t('filters.types.availability.tbo')}
    }

    render_table<Row extends PortalOrderItemResponse | SalesOrderItemResponse | SalesOrderTBOItemResponse>(
        collection: CollectionProxy<Row>,
        order: OrderType,
        is_cart = false,
    ) {
        const columns: CollectionColumnSetup<Row> = [
            {
                name: $t('order.details.product'),
                // Give the product the lion's share of the row — specs were getting truncated.
                width: 'minmax(0, 3fr)',
                render: (row) => (
                    <div className='td-group'>
                        <span className='header'>{row.product_name}</span>
                        <span className='details'>{to_specs(row)}</span>
                        <span className='details'>{row.case_article_code}</span>
                    </div>
                ),
            },
            {
                name: $t('order.details.price'),
                width: 'minmax(0, 1fr)',
                render: (row: OrderLinePrice) => {
                    const price = is_cart ? Number(row.list_price) : Number(row.price_per_case)
                    return (
                        <AmountUnit
                            case_amount={price}
                            case_number_of_bottles={row.case_number_of_bottles}
                            currency={order.sales_order.was_sold_in}
                            display_currency={order.sales_order.was_sold_in}
                        />
                    )
                },
            },
            {
                name: $t('order.details.quantity'),
                width: 'minmax(0, 1fr)',
                render: (record) => (
                    <CellBottleAndCase
                        bottle_text={
                            record.number_of_cases && record.case_number_of_bottles
                                ? `${record.number_of_cases * record.case_number_of_bottles} btl`
                                : ''
                        }
                        case_text={record.number_of_cases ? `${record.number_of_cases} cs` : ''}
                    />
                ),
            },
            {
                name: $t('order.details.subtotal'),
                width: 'minmax(0, 1fr)',
                render: (row: OrderLinePrice) => {
                    const price = is_cart ? Number(row.list_price) : Number(row.price_per_case)
                    const value = row.number_of_cases * price
                    return (
                        <div className='subtotal'>
                            {row.portal_comment && <Icon name='comment' size='s' tip={row.portal_comment} type='info' />}
                            {format_money_with_symbol(value, order.sales_order.was_sold_in)}
                        </div>
                    )
                },
            },
            // Explicit status column at the far right — keeps the icon pinned there
            // regardless of how often the column set is rebuilt.
            {
                className: 'cell-status',
                name: '',
                width: 'var(--coll-status-width)',
                render: () => null,
            },
        ]

        return (
            <CollectionView mode='table' view={false}>
                <CollectionItems
                    collection={collection}
                    columns={columns}
                    row_status={(row) => {
                        const {icon, type, label} = this.availability(row)
                        return {
                            render: <Icon name={icon} size='s' tip={label} type={type} />,
                        }
                    }}
                    row_status_no_cell_status={true}
                    type='table'
                />
            </CollectionView>
        )
    }

    soi_group(items: m.Children, title: string, icon: string, type: string) {
        if (!items) {
            return
        }
        return (
            <div className={classes('soi-group', type)}>
                <div className='title'>
                    <Icon name={icon} tip={$t('order.details.booked_context')} />
                    {title}
                </div>
                {items}
            </div>
        )
    }

    view_additionals(order: OrderType) {
        if (order.sales_order_additionals.length === 0) {
            return null
        }

        const columns: CollectionColumnSetup<SalesOrderAdditionalResponse> = [
            {
                name: $t('order.details.additional.type'),
                render: (row) => (
                    <div className='td-group'>
                        <span className='header'>{$t(`sales_order_addition_types.${row.sales_order_additional_type}`)}</span>
                        <span className='details'>{row.description}</span>
                    </div>
                ),
            },
            {
                name: $t('order.details.additional.price_unit'),
                render: (row) => format_money_with_symbol(row.price_per_unit, order.sales_order.was_sold_in),
            },
            {
                name: $t('order.details.additional.quantity'),
                render: (row) => row.quantity,
            },
            {
                name: $t('order.details.subtotal'),
                render: (row) => format_money_with_symbol(row.total, order.sales_order.was_sold_in),
            },
        ]
        return (
            <CollectionView mode='table' view={false}>
                <CollectionColumns collection={collection_additionals} columns={columns} />
                <CollectionItems collection={collection_additionals} columns={columns} />
            </CollectionView>
        )
    }

    view_booked(order: OrderType) {
        if (order.sales_order_items.length + order.sales_order_tbo_items.length === 0) {
            return null
        }

        return (
            <div className='c-table-container'>
                {collection_stock.state.items.length > 0 && [
                    <div className='collection-subtitle'>{$t('order.details.items.stock')}</div>,
                    this.render_table(collection_stock, order),
                ]}

                {collection_arriving.state.items.length > 0 && [
                    <div className='collection-subtitle'>{$t('order.details.items.arriving')}</div>,
                    this.render_table(collection_arriving, order),
                ]}

                {collection_tbo.state.items.length > 0 && [
                    <div className='collection-subtitle'>{$t('order.details.items.tbo')}</div>,
                    this.render_table(collection_tbo, order),
                ]}
            </div>
        )
    }

    view_cart(order: OrderType) {
        if (['Pending', 'Finalized'].indexOf(order.sales_order.portal_status) === -1) {
            return null
        }
        if (order.portal_order_items.length === 0) {
            return null
        }
        return <div className='c-table-container'>{this.render_table(collection_cart, order, true)}</div>
    }

    view_credit(order: OrderType) {
        if (order.sales_order_credit_items.length === 0) {
            return null
        }

        const columns: CollectionColumnSetup<SalesOrderCreditItemResponse> = [
            {
                className: 'column-product',
                name: $t('order.details.product'),
                render: (row) => (
                    <div className='td-group'>
                        <span className='header'>{row.product_name}</span>
                        <span className='details'>{to_specs(row)}</span>
                        <span className='details'>{row.case_article_code}</span>
                    </div>
                ),
            },
            {
                name: $t('order.details.credit.credited_from'),
                render: (row) => (
                    <Link href={`/shop/orders/${row.original_sales_order_artkey}`} remount={true}>
                        {`S${row.original_sales_order_artkey}`}
                    </Link>
                ),
            },
            {
                name: $t('order.details.price'),
                render: (row) => (
                    <AmountUnit
                        case_amount={Number(row.original_price_per_case) * -1}
                        case_number_of_bottles={row.case_number_of_bottles}
                        currency={order.sales_order.was_sold_in}
                        display_currency={order.sales_order.was_sold_in}
                    />
                ),
            },
            {
                name: $t('order.details.quantity'),
                render: (row) => {
                    const case_quantity = row.number_of_cases * -1
                    const bottle_quantity = case_quantity * row.case_number_of_bottles
                    return `${case_quantity} (${bottle_quantity})`
                },
            },
            {
                name: $t('order.details.subtotal'),
                render: (row) =>
                    format_money_with_symbol(
                        row.number_of_cases * Number(row.original_price_per_case) * -1,
                        order.sales_order.was_sold_in,
                    ),
            },
        ]

        return (
            <CollectionView mode='table' view={false}>
                <CollectionColumns collection={collection_credit} columns={columns} />
                <CollectionItems collection={collection_credit} columns={columns} />
            </CollectionView>
        )
    }

    view(vnode: m.Vnode<OrderLinesAttrs>) {
        const {order} = vnode.attrs
        const cart_items = order.portal_order_items
        const order_is_empty = [
            order.sales_order_items.length,
            order.sales_order_tbo_items.length,
            order.sales_order_additionals.length,
            order.sales_order_credit_items.length,
            cart_items.length,
        ]

        return (
            <div className='c-order-details'>
                {order_is_empty.every((i) => !i) && (
                    <div className='order-empty'>
                        <Icon name='cart' size='xxl' type='unset' />
                        <div>
                            <div className='title'>{$t('order.details.empty')}</div>
                            <div className='description'>
                                {$t('order.details.empty_context')}
                                <Button icon='cartRight' link={'/shop'} text={$t('order.empty_call_to_action')} type='info' />
                            </div>
                        </div>
                    </div>
                )}

                {this.soi_group(this.view_cart(order), $t('order.details.cart'), 'cartCheck', 'success')}
                {this.soi_group(this.view_booked(order), $t('order.details.booked'), 'cartRight', 'info')}
                {this.soi_group(this.view_additionals(order), $t('order.details.additionals'), 'offer', 'info')}
                {this.soi_group(this.view_credit(order), $t('order.details.credit.label'), 'cartUp', 'warning')}
            </div>
        )
    }
}
